<template>
  <v-dialog
    v-model="localChecklistDialog"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <v-card>
      <!-- App Bar -->
      <v-app-bar dark color="blue darken-3">
        <v-toolbar-title>Inspection Checklist</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="promptCloseDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-app-bar>

      <!-- Loader -->
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="blue"
        class="mt-5"
      ></v-progress-circular>

      <!-- Success Snackbar -->
      <v-snackbar v-model="showSuccessPrompt" color="green" top>
        Checklist saved successfully!
        <v-btn icon @click="showSuccessPrompt = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-snackbar>

      <!-- Validation Error Dialog -->
      <v-dialog v-model="validationErrorDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">Incomplete Checklist</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            Some checklist items are not answered. Please select "Yes" or "No" for all items before saving.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green" text @click="validationErrorDialog = false">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Checklist Content -->
      <v-card-text v-if="!loading && checklist.length">
        <v-container>
          <v-row dense>
            <v-col cols="12">
              <v-card
                v-for="(section, sectionIndex) in checklist"
                :key="sectionIndex"
                class="mb-4 elevation-2"
              >
                <v-card-title class="section-title">
                  {{ getChecklistHeader(section.sectionTitle) }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" class="checklist-header">
                      <h4>{{ getChecklistHeader(section.sectionTitle) }}</h4>
                    </v-col>

                    <v-col
                      cols="12"
                      v-for="(item, index) in section.items"
                      :key="index"
                      class="my-2"
                      :class="{ 'error-item': isItemIncomplete(item) }"
                    >
                      <v-row dense align="center">
                        <!-- Label Column -->
                        <v-col cols="6">
                          <span>{{ item.label }}</span>
                        </v-col>

                        <!-- Radio Group Column -->
                        <v-col cols="6">
                          <v-radio-group
                            v-model="item.checked"
                            :error="isItemIncomplete(item)"
                            row
                          >
                            <v-radio label="Yes" value="Yes"></v-radio>
                            <v-radio label="No" value="No"></v-radio>
                          </v-radio-group>
                        </v-col>
                      </v-row>

                      <!-- Conditional Text Fields -->
                      <v-row dense v-if="item.checked === 'Yes'">
                        <v-col cols="12">
                          <!-- Seal Fields -->
                          <v-text-field
                            v-if="item.showseal1"
                            v-model="item.seal1"
                            label="Any visible damages"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showJobNumberField"
                            v-model="item.jobNumberField"
                            label="Job Number"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showseal2"
                            v-model="item.seal2"
                            label="Special fitting on seal"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showseal3"
                            v-model="item.seal3"
                            label="Note Previous Job Number"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showseal4"
                            v-model="item.seal4"
                            label="Add photo of anything unusual"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>

                          <!-- Setting Clips Fields -->
                          <v-text-field
                            v-if="item.showsettingclipfield"
                            v-model="item.settingclipfield"
                            label="Setting Clip"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>

                          <!-- O-Rings Fields -->
                          <v-text-field
                            v-if="item.showoringMaterial"
                            v-model="item.oringMaterial"
                            label="O-Ring Material"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showoringdamage"
                            v-model="item.oringdamage"
                            label="O-Ring Damages"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showoringSize"
                            v-model="item.oringSize"
                            label="Size"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>

                          <!-- Inboard Faces Fields -->
                          <v-text-field
                            v-if="item.showFace1"
                            v-model="item.Face1"
                            label="Replace inboard stationary face"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showFace2"
                            v-model="item.Face2"
                            label="Replace inboard Rotary face"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showFace3"
                            v-model="item.Face3"
                            label="Result of vacuum test"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showFace4"
                            v-model="item.Face4"
                            label="Other Damage to faces"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>

                          <!-- Outboard Faces Fields -->
                          <v-text-field
                            v-if="item.showOutboardFace1"
                            v-model="item.OutboardFace1"
                            label="Replace outboard stationary face"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showOutboardFace2"
                            v-model="item.OutboardFace2"
                            label="Replace outboard Rotary Face"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showOutboardFace3"
                            v-model="item.OutboardFace3"
                            label="Result of Vacuum test"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>

                          <!-- Gland Fields -->
                          <v-text-field
                            v-if="item.showGland1"
                            v-model="item.Gland1"
                            label="O-ring surface acceptable"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland2"
                            v-model="item.Gland2"
                            label="Damage around setting clip area"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland3"
                            v-model="item.Gland3"
                            label="Damage on gasket/o-ring surface"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland4"
                            v-model="item.Gland4"
                            label="Pitting seen on gland"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland5"
                            v-model="item.Gland5"
                            label="Gland Port Size"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland6"
                            v-model="item.Gland6"
                            label="Spring holes damaged"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland7"
                            v-model="item.Gland7"
                            label="Dents/external damage"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland8"
                            v-model="item.Gland8"
                            label="Pins Bent loose, worn, or missing"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland9"
                            v-model="item.Gland9"
                            label="Other"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>

                          <!-- Additional Items Fields -->
                          <v-text-field
                            v-if="item.showadd1"
                            v-model="item.add1"
                            label="Lip Seals"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd2"
                            v-model="item.add2"
                            label="Tool Required 1"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd3"
                            v-model="item.add3"
                            label="Tool Required 2"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd4"
                            v-model="item.add4"
                            label="Tool Required 3"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd5"
                            v-model="item.add5"
                            label="Additional Part 1"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd6"
                            v-model="item.add6"
                            label="Additional Part 2"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd7"
                            v-model="item.add7"
                            label="Metal Bellows"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>

                          <!-- Checks Fields -->
                          <v-text-field
                            v-if="item.showcheck1"
                            v-model="item.check1"
                            label="Ovality on sealing surface"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showcheck2"
                            v-model="item.check2"
                            label="Damage on Other Parts"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showcheck3"
                            v-model="item.check3"
                            label="Additional Parts"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showcheck4"
                            v-model="item.check4"
                            label="Machining to be Done"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showcheck5"
                            v-model="item.check5"
                            label="Punch gland with new Job Number (if applicable)"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showcheck6"
                            v-model="item.check6"
                            label="Other parts and components"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Upload and View Photo Buttons -->
                    <v-col cols="12" class="d-flex justify-start">
                      <v-btn
                        rounded
                        dark
                        color="blue"
                        @click="openUploadOptionsDialog(sectionIndex)"
                        class="my-2"
                      >
                        <v-icon left>mdi-camera</v-icon>Upload Photo
                      </v-btn>

                      <v-btn
                        v-if="section.photos.length"
                        rounded
                        dark
                        color="green"
                        @click="openPhotoDialog(section.photos, section.sectionTitle)"
                        class="my-2 ml-2"
                      >
                        <v-icon left>mdi-eye</v-icon>View Photos
                      </v-btn>
                    </v-col>

                    <!-- Upload Loader -->
                    <v-col cols="12" v-if="uploadingPhoto">
                      <v-progress-linear
                        indeterminate
                        color="blue"
                        class="mb-2"
                      ></v-progress-linear>
                      <span>Uploading photos...</span>
                    </v-col>

                    <!-- Upload Photos Input -->
                    <input
                      :ref="'fileInput' + sectionIndex"
                      type="file"
                      accept="image/*"
                      multiple
                      @change="handleFileUpload($event, sectionIndex)"
                      style="display: none"
                    />
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <!-- Save and Close Buttons -->
      <v-card-actions v-if="!loading && !checklistSaved" class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          dark
          rounded
          dense
          width="150px"
          @click="promptSaveChecklist"
          color="blue"
        >
          Save Checklist
        </v-btn>
        <v-btn
          color="red"
          dark
          rounded
          dense
          width="150px"
          @click="promptCloseDialog"
        >
          Close
        </v-btn>
      </v-card-actions>

      <!-- Save Confirmation Dialog -->
      <v-dialog v-model="saveDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">Confirm Save</v-card-title>
          <v-divider></v-divider>
          <v-card-text>Are you sure you want to save the checklist?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green" text @click="saveChecklist">Yes</v-btn>
            <v-btn color="red" text @click="closeSaveDialog">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Close Confirmation Dialog -->
      <v-dialog v-model="closeDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">Confirm Close</v-card-title>
          <v-divider></v-divider>
          <v-card-text>Are you sure you want to close the checklist?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green" text @click="saveAndExit">Save and Exit</v-btn>
            <v-btn color="blue" text @click="closeCloseDialog">Return to Checklist</v-btn>
            <v-btn color="red" text @click="confirmCloseWithoutSaving">Exit without Saving</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Photo Dialog -->
      <v-dialog v-model="photoDialog" max-width="600px">
        <v-card>
          <v-toolbar dark color="primary">
            <v-toolbar-title>{{ currentPhotoLabel }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="closePhotoDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-carousel v-model="carouselIndex">
            <v-carousel-item
              v-for="(photo, index) in selectedPhotos"
              :key="index"
              :src="photo"
            ></v-carousel-item>
          </v-carousel>
        </v-card>
      </v-dialog>

      <!-- Upload Options Dialog -->
      <v-dialog v-model="uploadOptionsDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">Select Photo Upload Method</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-row class="pa-4" justify="center" align="center">
              <v-col cols="12">
                <v-btn
                  class="upload-btn my-2"
                  @click="takePhoto"
                  block
                  elevation="2"
                >
                  <v-icon left class="upload-icon">mdi-camera</v-icon>
                  Use Camera
                </v-btn>
              </v-col>
              <v-col cols="12">
                <v-btn
                  class="upload-btn my-2"
                  @click="uploadFromGallery"
                  block
                  elevation="2"
                >
                  <v-icon left class="upload-icon">mdi-image</v-icon>
                  Upload from Gallery
                </v-btn>
              </v-col>
              <v-col cols="12">
                <v-btn
                  class="upload-btn my-2"
                  @click="uploadFromFiles"
                  block
                  elevation="2"
                >
                  <v-icon left class="upload-icon">mdi-folder</v-icon>
                  Upload from Files
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text color="black" @click="closeUploadOptionsDialog">
              Cancel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
  uploadBytes,
} from "firebase/storage";
import { firebaseApp } from "@/firebaseConfig";

export default {
  props: {
    jobNumber: {
      type: String,
      required: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localChecklistDialog: this.value,
      checklist: [],
      checklistDocId: null,
      loading: true,
      uploadingPhoto: false,
      selectedPhotos: [],
      photoDialog: false,
      currentPhotoLabel: '',
      uploadOptionsDialog: false,
      selectedSectionIndex: null,
      saveDialog: false,
      closeDialog: false,
      showSuccessPrompt: false,
      validationErrorDialog: false,
      photoLabels: [],
      carouselIndex: 0,
      checklistSaved: false,
      showErrorMessages: false, // To control error highlighting
      defaultChecklist: [
        {
          sectionTitle: "NOTE",
          items: [
            { label: "Seal Failure Report Requested?", checked: null },
            { label: "Any visible damage?", checked: null, upload: true, showseal1: true, seal1: "" },
            { label: "Special Fittings on seal?", checked: null, upload: true, showseal2: true, seal2: "" },
            { label: "Note Previous Job Number", checked: null, showJobNumberField: true, jobNumberField: "" },
            { label: "Add Photo of seal as received", checked: null, upload: true },
            { label: "Add Photo of anything unusual", checked: null, upload: true, showseal3: true, seal3: "" },
          ],
          photos: [],
        },
        {
          sectionTitle: "SETTING CLIPS",
          items: [
            { label: "Setting Clips present", checked: null, upload: true },
            { label: "Setting Clips reusable", checked: null, showsettingclipfield: true, settingclipfield: "" },
          ],
          photos: [],
        },
        {
          sectionTitle: "O-RINGS",
          items: [
            { label: "O-ring Material", checked: null, showoringMaterial: true, oringMaterial: "" },
            { label: "O-ring damages", checked: null, upload: true, showoringdamage: true, oringdamage: "" },
            { label: "O-ring Sizes", checked: null, upload: true, showoringSize: true, oringSize: "" },
          ],
          photos: [],
        },
        {
          sectionTitle: "INBOARD FACES",
          items: [
            { label: "Replace inboard stationary face", checked: null, upload: true, showFace1: true, Face1: "" },
            { label: "Replace inboard Rotary face", checked: null, upload: true, showFace2: true, Face2: "" },
            { label: "Result of Vacuum test (if required)", checked: null, showFace3: true, Face3: "" },
            { label: "Other Damage on faces", checked: null, upload: true, showFace4: true, Face4: "" },
          ],
          photos: [],
        },
        {
          sectionTitle: "OUTBOARD FACES (if applicable)",
          items: [
            { label: "Replace outboard stationary face", checked: null, upload: true, showOutboardFace1: true, OutboardFace1: "" },
            { label: "Replace outboard Rotary face", checked: null, upload: true, showOutboardFace2: true, OutboardFace2: "" },
            { label: "Result of Vacuum test (if required)", checked: null, showOutboardFace3: true, OutboardFace3: "" },
            { label: "Other Damage on faces", checked: null, upload: true, showOutboardFace4: true, OutboardFace4: "" },
          ],
          photos: [],
        },
        {
          sectionTitle: "GLAND",
          items: [
            { label: "O-ring surfaces acceptable", checked: null, upload: true, showGland1: true, Gland1: "" },
            { label: "Damage around setting clip area", checked: null, upload: true, showGland2: true, Gland2: "" },
            { label: "Damage on gasket/o-ring surface", checked: null, upload: true, showGland3: true, Gland3: "" },
            { label: "Pitting seen on gland", checked: null, upload: true, showGland4: true, Gland4: "" },
            { label: "Gland port sizes", checked: null, upload: true, showGland5: true, Gland5: "" },
            { label: "Spring holes damaged", checked: null, upload: true, showGland6: true, Gland6: "" },
            { label: "Dents/external damage", checked: null, upload: true, showGland7: true, Gland7: "" },
            { label: "Pins: Bent, Loose, worn, missing", checked: null, upload: true, showGland8: true, Gland8: "" },
            { label: "Other", checked: null, upload: true, showGland9: true, Gland9: "" },
          ],
          photos: [],
        },
        {
          sectionTitle: "ADDITIONAL ITEMS",
          items: [
            { label: "Lip seals", checked: null, upload: true, showadd1: true, add1: "" },
            { label: "Tool required 1", checked: null, upload: true, showadd2: true, add2: "" },
            { label: "Tool required 2", checked: null, upload: true, showadd3: true, add3: "" },
            { label: "Tool required 3", checked: null, upload: true, showadd4: true, add4: "" },
            { label: "Additional part 1", checked: null, upload: true, showadd5: true, add5: "" },
            { label: "Additional part 2", checked: null, upload: true, showadd6: true, add6: "" },
            { label: "Metal bellows", checked: null, upload: true, showadd7: true, add7: "" },
            { label: "Other", checked: null, upload: true, showadd8: true, add8: "" },
          ],
          photos: [],
        },
        {
          sectionTitle: "CHECKS",
          items: [
            { label: "Ovality on sealing surfaces", checked: null, upload: true, showcheck1: true, check1: "" },
            { label: "Damage on other parts", checked: null, upload: true, showcheck2: true, check2: "" },
            { label: "Additional parts (Shaft sleeve, Backplate, etc.)", checked: null, upload: true, showcheck3: true, check3: "" },
            { label: "Note of machining to be done", checked: null, upload: true, showcheck4: true, check4: "" },
            { label: "Punch gland with new Job Number (if applicable)", checked: null, upload: true, showcheck5: true, check5: "" },
            { label: "Other parts or components", checked: null, upload: true, showcheck6: true, check6: "" },
          ],
          photos: [],
        },
      ],
    };
  },
  watch: {
    value(val) {
      this.localChecklistDialog = val;
      if (val) {
        this.fetchChecklist();
      }
    },
    localChecklistDialog(val) {
      this.$emit("input", val);
    },
    carouselIndex(newIndex) {
      this.currentPhotoLabel = this.photoLabels[newIndex] || "";
    },
  },
  computed: {
    isFormValid() {
      // Only validate when user attempts to save
      return this.checklist.every(section =>
        section.items.every(item => item.checked === "Yes" || item.checked === "No")
      );
    },
  },
  methods: {
    isItemIncomplete(item) {
      return this.showErrorMessages && !(item.checked === "Yes" || item.checked === "No");
    },
    handleCheckboxChange(item, value) {
      // This method is no longer needed as we're using radio groups
    },
    clearDependentFields(item) {
      for (const key in item) {
        if (key.startsWith("show") && key !== "showJobNumberField") {
          this.$set(item, key, false);
        }
        if (
          key.startsWith("seal") ||
          key.startsWith("Gland") ||
          key.startsWith("add") ||
          key.startsWith("check") ||
          key === "settingclipfield" ||
          key === "oringMaterial" ||
          key === "oringdamage" ||
          key === "oringSize" ||
          key.startsWith("Face") ||
          key.startsWith("OutboardFace")
        ) {
          this.$set(item, key, "");
        }
      }
    },
    toggleFieldVisibility(item) {
      const label = item.label;
      const visibilityMap = {
        "Special Fittings on seal?": "showseal2",
        "Note Previous Job Number": "showJobNumberField",
        "Add Photo of anything unusual": "showseal4",
        "Setting Clips reusable": "showsettingclipfield",
        "O-ring Material": "showoringMaterial",
        "O-ring damages": "showoringdamage",
        "O-ring Sizes": "showoringSize",
        "Replace inboard stationary face": "showFace1",
        "Replace inboard Rotary face": "showFace2",
        "Result of Vacuum test (if required)": ["showFace3", "showOutboardFace3"],
        "Other Damage on faces": ["showFace4", "showOutboardFace4"],
        "Replace outboard stationary face": "showOutboardFace1",
        "Replace outboard Rotary face": "showOutboardFace2",
        "O-ring surfaces acceptable": "showGland1",
        "Damage around setting clip area": "showGland2",
        "Damage on gasket/o-ring surface": "showGland3",
        "Pitting seen on gland": "showGland4",
        "Gland port sizes": "showGland5",
        "Spring holes damaged": "showGland6",
        "Dents/external damage": "showGland7",
        "Pins: Bent, Loose, worn, missing": "showGland8",
        "Other": "showGland9",
        "Lip seals": "showadd1",
        "Tool required 1": "showadd2",
        "Tool required 2": "showadd3",
        "Tool required 3": "showadd4",
        "Additional part 1": "showadd5",
        "Additional part 2": "showadd6",
        "Metal bellows": "showadd7",
        "Ovality on sealing surfaces": "showcheck1",
        "Damage on other parts": "showcheck2",
        "Additional parts (Shaft sleeve, Backplate, etc.)": "showcheck3",
        "Note of machining to be done": "showcheck4",
        "Punch gland with new Job Number (if applicable)": "showcheck5",
        "Other parts or components": "showcheck6",
      };

      if (visibilityMap[label]) {
        const fields = Array.isArray(visibilityMap[label])
          ? visibilityMap[label]
          : [visibilityMap[label]];
        fields.forEach((field) => {
          this.$set(item, field, item.checked === "Yes");
        });
      }
    },
    openPhotoDialog(photos, label) {
      this.selectedPhotos = photos;
      this.photoLabels = new Array(photos.length).fill(label);
      this.currentPhotoLabel = label;
      this.carouselIndex = 0;
      this.photoDialog = true;
    },
    closePhotoDialog() {
      this.photoDialog = false;
    },
    async fetchChecklist() {
      this.loading = true;
      try {
        const db = getFirestore(firebaseApp);
        const checklistRef = collection(db, "checklists");
        const q = query(checklistRef, where("jobNumber", "==", this.jobNumber));
        const querySnapshot = await getDocs(q);

        // Always start from default checklist structure
        this.checklist = JSON.parse(JSON.stringify(this.defaultChecklist));

        if (!querySnapshot.empty) {
          const docSnapshot = querySnapshot.docs[0];
          const docData = docSnapshot.data();
          this.checklistDocId = docSnapshot.id;

          // Update only checked states and related fields for matching items
          docData.checklist.forEach((savedSection) => {
            const localSection = this.checklist.find(s => s.sectionTitle === savedSection.sectionTitle);
            if (!localSection) return;

            savedSection.items.forEach((savedItem) => {
              const localItem = localSection.items.find(i => i.label === savedItem.label);
              if (!localItem) return;

              // Update checked value and fields if present in default structure
              if (savedItem.checked) localItem.checked = savedItem.checked;

              // Copy over any fields that are present in localItem
              for (const key in savedItem) {
                if (key !== 'label' && key !== 'checked' && key in localItem) {
                  localItem[key] = savedItem[key];
                }
              }
            });

            // Update photos if available
            if (savedSection.photos && Array.isArray(savedSection.photos)) {
              localSection.photos = savedSection.photos;
            }
          });
        }
      } catch (error) {
        console.error("Error fetching checklist:", error);
      } finally {
        this.loading = false;
      }
    },
    getChecklistHeader(sectionTitle) {
      const headers = {
        NOTE: "Note",
        "SETTING CLIPS": "Setting Clips",
        "O-RINGS": "O-Rings",
        "INBOARD FACES": "Inboard Faces",
        "OUTBOARD FACES (if applicable)": "Outboard Faces",
        GLAND: "Gland",
        "ADDITIONAL ITEMS": "Additional Items",
        CHECKS: "Checks",
      };
      return headers[sectionTitle] || "Checklist Details";
    },
    promptSaveChecklist() {
      if (this.isFormValid) {
        this.saveDialog = true;
      } else {
        this.showErrorMessages = true;
        this.validationErrorDialog = true;
      }
    },
    async saveAndExit() {
      if (!this.isFormValid) {
        this.showErrorMessages = true;
        this.validationErrorDialog = true;
        return;
      }
      await this.saveChecklist();
      this.localChecklistDialog = false;
      this.$emit('input', false);
      this.closeDialog = false;
    },
    confirmCloseWithoutSaving() {
      this.localChecklistDialog = false;
      this.$emit('input', false);
      this.closeDialog = false;
    },
    closeCloseDialog() {
      this.closeDialog = false;
    },
    async saveChecklist() {
      if (!this.isFormValid) {
        this.showErrorMessages = true;
        this.validationErrorDialog = true;
        return;
      }
      const db = getFirestore(firebaseApp);

      // Prepare the checklist data for saving
      const checklistData = this.checklist.map((section) => ({
        sectionTitle: section.sectionTitle,
        items: section.items.map((item) => {
          const savedItem = {
            label: item.label,
            checked: item.checked,
          };

          // Include fields if present and relevant
          if (item.showseal1 && item.seal1) savedItem.seal1 = item.seal1;
          if (item.showJobNumberField && item.jobNumberField) savedItem.jobNumberField = item.jobNumberField;
          if (item.showseal2 && item.seal2) savedItem.seal2 = item.seal2;
          if (item.showseal3 && item.seal3) savedItem.seal3 = item.seal3;
          if (item.showseal4 && item.seal4) savedItem.seal4 = item.seal4;
          if (item.showsettingclipfield && item.settingclipfield) savedItem.settingclipfield = item.settingclipfield;
          if (item.showoringMaterial && item.oringMaterial) savedItem.oringMaterial = item.oringMaterial;
          if (item.showoringdamage && item.oringdamage) savedItem.oringdamage = item.oringdamage;
          if (item.showoringSize && item.oringSize) savedItem.oringSize = item.oringSize;
          if (item.showFace1 && item.Face1) savedItem.Face1 = item.Face1;
          if (item.showFace2 && item.Face2) savedItem.Face2 = item.Face2;
          if (item.showFace3 && item.Face3) savedItem.Face3 = item.Face3;
          if (item.showFace4 && item.Face4) savedItem.Face4 = item.Face4;
          if (item.showOutboardFace1 && item.OutboardFace1) savedItem.OutboardFace1 = item.OutboardFace1;
          if (item.showOutboardFace2 && item.OutboardFace2) savedItem.OutboardFace2 = item.OutboardFace2;
          if (item.showOutboardFace3 && item.OutboardFace3) savedItem.OutboardFace3 = item.OutboardFace3;
          if (item.showOutboardFace4 && item.OutboardFace4) savedItem.OutboardFace4 = item.OutboardFace4;
          if (item.showGland1 && item.Gland1) savedItem.Gland1 = item.Gland1;
          if (item.showGland2 && item.Gland2) savedItem.Gland2 = item.Gland2;
          if (item.showGland3 && item.Gland3) savedItem.Gland3 = item.Gland3;
          if (item.showGland4 && item.Gland4) savedItem.Gland4 = item.Gland4;
          if (item.showGland5 && item.Gland5) savedItem.Gland5 = item.Gland5;
          if (item.showGland6 && item.Gland6) savedItem.Gland6 = item.Gland6;
          if (item.showGland7 && item.Gland7) savedItem.Gland7 = item.Gland7;
          if (item.showGland8 && item.Gland8) savedItem.Gland8 = item.Gland8;
          if (item.showGland9 && item.Gland9) savedItem.Gland9 = item.Gland9;
          if (item.showadd1 && item.add1) savedItem.add1 = item.add1;
          if (item.showadd2 && item.add2) savedItem.add2 = item.add2;
          if (item.showadd3 && item.add3) savedItem.add3 = item.add3;
          if (item.showadd4 && item.add4) savedItem.add4 = item.add4;
          if (item.showadd5 && item.add5) savedItem.add5 = item.add5;
          if (item.showadd6 && item.add6) savedItem.add6 = item.add6;
          if (item.showadd7 && item.add7) savedItem.add7 = item.add7;
          if (item.showcheck1 && item.check1) savedItem.check1 = item.check1;
          if (item.showcheck2 && item.check2) savedItem.check2 = item.check2;
          if (item.showcheck3 && item.check3) savedItem.check3 = item.check3;
          if (item.showcheck4 && item.check4) savedItem.check4 = item.check4;
          if (item.showcheck5 && item.check5) savedItem.check5 = item.check5;
          if (item.showcheck6 && item.check6) savedItem.check6 = item.check6;

          return savedItem;
        }),
        photos: section.photos || [],
      }));

      const checklistDoc = {
        jobNumber: this.jobNumber,
        checklist: checklistData,
      };

      try {
        if (this.checklistDocId) {
          // Update existing checklist
          const checklistRef = doc(db, "checklists", this.checklistDocId);
          await updateDoc(checklistRef, { checklist: checklistData });
        } else {
          // Add new checklist
          await addDoc(collection(db, "checklists"), checklistDoc);
        }
        this.showSuccessPrompt = true;
        this.checklistSaved = true;
        this.saveDialog = false;
        this.localChecklistDialog = false;
      } catch (error) {
        console.error("Error saving checklist:", error);
        this.validationErrorDialog = true;
      }
    },
    closeSaveDialog() {
      this.saveDialog = false;
    },
    openUploadOptionsDialog(sectionIndex) {
      this.selectedSectionIndex = sectionIndex;
      this.uploadOptionsDialog = true;
    },
    closeUploadOptionsDialog() {
      this.uploadOptionsDialog = false;
    },
    async takePhoto() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.setAttribute("capture", "camera");
      fileInput.click();
    },
    uploadFromGallery() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.removeAttribute("capture");
      fileInput.setAttribute("accept", "image/*");
      fileInput.click();
    },
    uploadFromFiles() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.removeAttribute("capture");
      fileInput.setAttribute("accept", "*/*");
      fileInput.click();
    },
    async handleFileUpload(event, sectionIndex) {
      const files = event.target.files;
      const storage = getStorage(firebaseApp);
      const photos = [];

      this.uploadingPhoto = true;

      try {
        for (const file of files) {
          const uniqueFileName = `${Date.now()}_${file.name}`;
          const storageReference = storageRef(storage, `checklists/${this.jobNumber}/${uniqueFileName}`);
          const snapshot = await uploadBytes(storageReference, file);
          const downloadURL = await getDownloadURL(snapshot.ref);
          photos.push(downloadURL);
        }
        this.checklist[sectionIndex].photos.push(...photos);
      } catch (error) {
        console.error("Error uploading files:", error);
        this.validationErrorDialog = true;
      } finally {
        this.uploadingPhoto = false;
      }
    },
    promptCloseDialog() {
      this.closeDialog = true;
    },
  },
};
</script>

<style scoped>
.section-title {
  font-size: 24px;
  font-weight: bold;
  color: #1976d2;
  margin-top: 16px;
  margin-bottom: 10px;
}

.checklist-header {
  margin-top: 20px;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 18px;
  color: #424242;
}

.v-card {
  border-radius: 16px;
  background-color: #fafafa;
}

.v-btn {
  font-size: 14px;
  font-weight: bold;
}

.v-toolbar-title {
  font-weight: bold;
  font-size: 22px;
}

.v-progress-circular {
  margin-top: 50px;
}

.v-progress-linear {
  margin-top: 10px;
}

.upload-btn {
  background-color: #ffffff;
  color: #000000;
  font-size: 16px;
  padding: 12px;
  border: 1px solid #cccccc;
}

.upload-btn:hover {
  background-color: #f5f5f5;
}

.upload-icon {
  color: #000000;
  margin-right: 10px;
}

/* Highlight incomplete items */
.error-item {
  border-left: 4px solid red;
  padding-left: 8px;
  background-color: #ffe6e6;
}
</style>
